/* eslint-disable jsx-a11y/label-has-associated-control */
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { RD_COMPONENTS, RdComponentType } from '../../_utils';
import { Typography } from '../../typography';
import { InputText } from '../input-text';
import styles from './input-file.module.scss';

export interface FileValidationTypes {
  fileTypesValidation?: {
    /** allowedExtensions accepts an array of file extensions to be validated on file upload */
    allowedExtensions: string[];

    /** Error message when uploaded file type does not match any item in allowedExtensions array */
    errMessage: string;
  };
  maxFileSizeValidation?: {
    /** Max size in KBs allowed for the file to be uploaded */
    maxSize: number;

    /** Error message when uploaded file size is greater than maxSize specified */
    errMessage: string;
  };
}

export interface InputFileComponentProps {
  /**
   * input file container class name
   */
  className?: string;

  /**
   * show error state
   * @default false
   */
  hasError?: boolean;

  /**
   * error message/text
   */
  helperText?: string;

  /**
   * Text which will be shown in button
   * @default Upload
   */
  label?: string;

  /**
   * The callback function that is triggered when file selection changed
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, files: FileList | never[]) => void;

  /** Validations to be checked and error messages to be added when the validation fails at time of file upload. */
  validation?: FileValidationTypes;

  /**
   * To show the selected file name in edit form
   */
  value?: string;
}

export type InputFileProps = InputFileComponentProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'>;

export const InputFile: RdComponentType<InputFileProps> = ({
  className,
  placeholder,
  value = '',
  hasError = false,
  helperText,
  label = 'Upload',
  disabled,
  onChange,
  validation,
  ...restProps
}: InputFileProps) => {
  const [selectedFiles, setSelectedFiles] = useState<string>(value);
  // errText is set to helperText or to validation error message if validation check fails.
  const [errText, setErrText] = useState<string>(helperText || '');
  // isErrorPresent is set to true when either parent sets it to true using hasError prop or when the validation check fails.
  const [isErrorPresent, setIsErrorPresent] = useState<boolean>(hasError);
  const containerClassNames = cx(className, styles.container);

  useEffect(() => {
    setSelectedFiles(value);
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | never[] = (event.target as HTMLInputElement).files || [];
    const fileNamesArr: string[] = [];
    let fileName = '';
    let isValidationError = false;
    let validationErrMsg = '';

    if (files.length) {
      for (let i = 0; i < files.length; i++) {
        const { size, name } = files[i];
        const currentFileSizeinKbs = size / 1000;
        const curentFileExtension = name.substr(name.lastIndexOf('.') + 1, name.length);
        if (validation) {
          const { maxFileSizeValidation, fileTypesValidation } = validation;
          const isFileTypesValidationError =
            fileTypesValidation &&
            !fileTypesValidation.allowedExtensions.includes(curentFileExtension);
          const isMaxSizeValidationError =
            maxFileSizeValidation && currentFileSizeinKbs > maxFileSizeValidation.maxSize;

          if (isFileTypesValidationError) {
            isValidationError = true;
            validationErrMsg = fileTypesValidation!.errMessage;
          } else if (isMaxSizeValidationError) {
            isValidationError = true;
            validationErrMsg = maxFileSizeValidation!.errMessage;
          }
        }

        fileNamesArr.push(name);
      }

      fileName =
        fileNamesArr.length === 1
          ? fileNamesArr[0]
          : `${fileNamesArr[0]} + ${fileNamesArr.length - 1}`;
    }

    setSelectedFiles(fileName);
    setIsErrorPresent(isValidationError);
    const finalErrorText = validationErrMsg || helperText || '';
    setErrText(finalErrorText);

    if (onChange && !isValidationError) {
      onChange(event, files);
    }
  };

  return (
    <div data-radiant-v2 className={containerClassNames}>
      <div className={styles.inputContainer}>
        <InputText
          ariaLabel={placeholder || 'Select File'}
          hasError={isErrorPresent}
          helperText={errText}
          placeholder={placeholder || 'Select File'}
          disabled={disabled}
          value={selectedFiles}
          readOnly
        />
      </div>
      <label className={styles.label}>
        <Typography variant="body-normal">{label}</Typography>
        <input
          type="file"
          className={styles.inputFile}
          disabled={disabled}
          onChange={handleChange}
          accept={
            validation && validation.fileTypesValidation
              ? `.${validation.fileTypesValidation.allowedExtensions.join(',.')}`
              : ''
          }
          {...restProps}
        />
      </label>
    </div>
  );
};

InputFile.RD_COMPONENT = RD_COMPONENTS.INPUT_FILE;
